import React, { useState } from "react";
import i18n from "@/utils/i18n";
import Button from "@/components/ui/button";
import { Download as DownloadIcon } from "@/components/icons";
import { toast } from "sonner";
import styles from "./ReportExportButtons.module.scss";

interface ReportExportButtonsProps {
  reportType: string;
  filters?: Record<string, unknown>;
  filenameBase?: string;
  exportFn: (payload: {
    reportType: string;
    format: "xlsx" | "pdf";
    filters?: Record<string, unknown>;
  }) => Promise<Blob>;
  disabled?: boolean;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const ReportExportButtons: React.FC<ReportExportButtonsProps> = ({
  reportType,
  filters,
  filenameBase = "report",
  exportFn,
  disabled,
}) => {
  const [loadingFormat, setLoadingFormat] = useState<"xlsx" | "pdf" | null>(
    null,
  );

  const handleExport = async (format: "xlsx" | "pdf") => {
    if (loadingFormat || disabled) return;
    setLoadingFormat(format);
    try {
      const blob = await exportFn({ reportType, format, filters });
      const ext = format === "xlsx" ? "xlsx" : "pdf";
      const stamp = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `${filenameBase}-${stamp}.${ext}`);
    } catch {
      toast.error(i18n.t("reports.exportError"));
    } finally {
      setLoadingFormat(null);
    }
  };

  return (
    <div className={styles.wrap}>
      <Button
        variant="outlined"
        color="secondary"
        type="button"
        onClick={() => handleExport("xlsx")}
        disabled={disabled || loadingFormat !== null}
        startIcon={<DownloadIcon width={16} height={16} />}
      >
        {loadingFormat === "xlsx" ? "…" : "Excel"}
      </Button>
      <Button
        variant="outlined"
        color="secondary"
        type="button"
        onClick={() => handleExport("pdf")}
        disabled={disabled || loadingFormat !== null}
        startIcon={<DownloadIcon width={16} height={16} />}
      >
        {loadingFormat === "pdf" ? "…" : "PDF"}
      </Button>
    </div>
  );
};

export default ReportExportButtons;
